import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { MatchingFaq } from "@/components/public/MatchingFaq";
import { IntakeForm } from "@/components/public/IntakeForm";
import { SectionBackground } from "@/components/cms/SectionBackground";
import { RichTextView } from "@/components/cms/RichText";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Phone, MessageCircle, CalendarHeart, Check, ArrowRight, Loader2, HelpCircle,
  Users, Handshake, MapPin, HeartHandshake, ShieldCheck, Sparkles,
} from "lucide-react";

const CONTAINER = "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

function useSeo({ title, description, canonical, noindex, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    const set = (sel, create) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const desc = set('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" }));
    if (description) desc.setAttribute("content", description);
    const can = set('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }));
    if (canonical) can.setAttribute("href", canonical);
    const robots = set('meta[name="robots"]', () => Object.assign(document.createElement("meta"), { name: "robots" }));
    robots.setAttribute("content", noindex ? "noindex,nofollow" : "index,follow");
    document.querySelectorAll('script[data-mmm-jsonld]').forEach((s) => s.remove());
    if (jsonLd && jsonLd.length) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-mmm-jsonld", "1");
      s.textContent = JSON.stringify(jsonLd.length === 1 ? jsonLd[0] : jsonLd);
      document.head.appendChild(s);
    }
    return () => { document.querySelectorAll('script[data-mmm-jsonld]').forEach((s) => s.remove()); };
  }, [title, description, canonical, noindex, jsonLd]);
}

const Bullets = ({ items, icon: Icon = Check, tone = "#2F7D5A" }) => (
  <ul className="space-y-3">
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${tone}1f`, color: tone }}><Icon size={15} /></span>
        <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ id, title, eyebrow, children, bg }) => (
  <section id={id} className={`py-12 sm:py-16 ${bg || ""}`}>
    <div className={CONTAINER}>
      {eyebrow && <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">{eyebrow}</p>}
      {title && <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">{title}</h2>}
      <div className="mt-6">{children}</div>
    </div>
  </section>
);

export default function ReverseSearchPage() {
  const { topic, location: locSlug } = useParams();
  const { content, openChat } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [matchedFaqs, setMatchedFaqs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      let vid = localStorage.getItem("mmm_vid");
      if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("mmm_vid", vid); }
      const { data } = await publicApi.get(`/public/find/${topic}`, { params: { ...(locSlug ? { location: locSlug } : {}), vid } });
      setData(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [topic, locSlug]);

  useEffect(() => { load(); }, [load]);

  const p = data?.page;
  const ab = data?.ab || {};
  const mergedJsonLd = useMemo(() => {
    if (!matchedFaqs.length) return data?.json_ld;
    const base = Array.isArray(data?.json_ld) ? data.json_ld.map((x) => ({ ...x })) : [];
    const entities = matchedFaqs.map((f) => ({
      "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer },
    }));
    const idx = base.findIndex((x) => x && x["@type"] === "FAQPage");
    if (idx >= 0) {
      const existing = Array.isArray(base[idx].mainEntity) ? base[idx].mainEntity : [];
      base[idx] = { ...base[idx], mainEntity: [...existing, ...entities] };
    } else {
      base.push({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: entities });
    }
    return base;
  }, [data, matchedFaqs]);
  useSeo({
    title: p?.seo_title, description: p?.seo_description, canonical: data?.canonical,
    noindex: data?.noindex, jsonLd: mergedJsonLd,
  });

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (notFound || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="reverse-not-found">
        <h1 className="text-3xl font-display font-bold text-[#1C4358]">We couldn't find that page.</h1>
        <Link to="/" className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-full text-white bg-[#9C1E22] font-semibold">Back home <ArrowRight size={16} /></Link>
      </div>
    );
  }

  const fr = data.franchise;
  const lane = data.lane;
  const phone = fr?.phone || content?.phone;
  const headline = ab.headline || p.hero_headline;
  const heroImg = ab.hero_image || p.hero_image_url;
  const ctaLabel = ab.cta_label || p.cta_primary_label;

  return (
    <div data-testid="reverse-search-page" data-topic={p.slug} data-variation={ab.variation}>
      {/* Hero */}
      <SectionBackground
        bg={p.hero_background}
        fallbackImage={heroImg}
        fallbackAlt={p.hero_image_alt || p.admin_title}
        testid="reverse-hero"
        innerClassName={`${CONTAINER} py-20 sm:py-28`}
      >
          <div className="max-w-2xl mmm-fade-up">
            {p.location_headline_suffix && (
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-[13px] font-semibold text-white" data-testid="reverse-location-badge">
                <MapPin size={14} className="text-[#DEE029]" /> {p.location_headline_suffix}
              </p>
            )}
            <h1 className="mt-4 text-4xl sm:text-5xl font-display font-bold tracking-tight text-white leading-[1.1]">{headline}</h1>
            <p className="mt-5 text-lg sm:text-xl text-white/90 leading-relaxed">{p.hero_subheadline}</p>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <a href="#rs-form" data-testid="reverse-book-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors shadow-lg shadow-black/20">
                <CalendarHeart size={20} /> {ctaLabel}
              </a>
              <a href={telHref(phone)} data-testid="reverse-call-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white hover:bg-white/90 transition-colors">
                <Phone size={19} /> Call {phone}
              </a>
              <button type="button" onClick={() => openChat && openChat({ cta_source: "Reverse Search Chat", reverse_search_topic: p.slug, matched_service_lane: p.matched_service_lane, location_slug: data.location?.slug || null, franchise: fr?.subdomain || null })} data-testid="reverse-chat-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-white border-2 border-white/70 hover:bg-white/10 transition-colors">
                <MessageCircle size={19} /> {p.cta_chat_label}
              </button>
            </div>
          </div>
      </SectionBackground>

      {/* Reassurance */}
      <div className="bg-[#FBF6EC]"><div className={`${CONTAINER} py-10 text-center`}>
        <p className="text-2xl sm:text-3xl font-display text-[#1C4358] leading-relaxed max-w-3xl mx-auto">{p.reassurance}</p>
      </div></div>

      {/* Problem explanation */}
      {(p.problem_explanation_html || p.problem_explanation) && (
        <Block eyebrow="We understand" title="What you're facing">
          {p.problem_explanation_html ? (
            <RichTextView html={p.problem_explanation_html} className="text-lg text-[#4B5563] leading-relaxed max-w-3xl" testid="reverse-problem" />
          ) : (
            <p className="text-lg text-[#4B5563] leading-relaxed max-w-3xl">{p.problem_explanation}</p>
          )}
        </Block>
      )}

      {/* 3-step process */}
      <Block eyebrow="Simple & calm" title="How it works" bg="bg-[#1C4358] mmm-grain">
        <div className="grid gap-8 md:grid-cols-3 -mt-2">
          {(p.steps || []).map((s) => (
            <div key={s.n} data-testid={`reverse-step-${s.n}`} className="rounded-[0.9rem] bg-white/5 border border-white/10 p-7">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#DEE029] text-[#1C4358] text-xl font-display font-bold">{s.n}</div>
              <h3 className="mt-4 text-xl font-display font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-[16px] text-white/80 leading-relaxed">{s.description}</p>
            </div>
          ))}
        </div>
      </Block>

      {/* What we help with */}
      <Block eyebrow="How we help" title="What MovingMyMama helps with">
        <div className="grid gap-4 sm:grid-cols-2">
          {(p.we_help_with || []).map((t, i) => (
            <div key={i} className="flex items-start gap-3 rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2F7D5A]/12 text-[#2F7D5A]"><Check size={17} /></span>
              <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
      </Block>

      {/* Local sections */}
      {fr && (fr.team?.length > 0 || fr.partners?.length > 0 || fr.mapped_area) && (
        <Block eyebrow="In your area" title={`Local support${fr.name ? ` — ${fr.name}` : ""}`} bg="bg-[#C4C89B]/25">
          {fr.mapped_area && <p className="flex items-center gap-2 text-[17px] text-[#1C4358] font-medium mb-6"><MapPin size={18} className="text-[#9C1E22]" /> {fr.mapped_area}</p>}
          {fr.team?.length > 0 && (
            <div className="mb-8" data-testid="reverse-local-team">
              <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-4">Your local team</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fr.team.map((m, i) => (
                  <div key={i} className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
                    <div className="font-display font-semibold text-[#1C4358] text-lg">{m.name}</div>
                    {m.title && <div className="text-[14px] text-[#4B5563]">{m.title}</div>}
                    {m.phone && <a href={telHref(m.phone)} className="mt-2 flex items-center gap-2 text-[14px] text-[#1C4358]"><Phone size={14} /> {m.phone}</a>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {fr.partners?.length > 0 && (
            <div data-testid="reverse-local-partners">
              <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-4">Trusted local partners</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fr.partners.map((pt, i) => (
                  <div key={i} className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
                    <div className="font-display font-semibold text-[#1C4358]">{pt.name}</div>
                    {pt.type && <div className="text-[14px] text-[#9C1E22] font-medium">{pt.type}</div>}
                    {pt.phone && <a href={telHref(pt.phone)} className="mt-2 flex items-center gap-2 text-[14px] text-[#1C4358]"><Phone size={14} /> {pt.phone}</a>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Block>
      )}

      {/* Trust / proof */}
      {p.trust_points?.length > 0 && (
        <Block eyebrow="Why families trust us" title="You're in good hands" bg="bg-[#9BB9CE]/15">
          <div className="grid gap-4 sm:grid-cols-2">
            {p.trust_points.map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <ShieldCheck size={22} className="text-[#2F7D5A] shrink-0 mt-0.5" />
                <span className="text-[17px] text-[#252221]">{t}</span>
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* FAQs */}
      {p.common_questions?.length > 0 && (
        <Block eyebrow="Common questions" title="Answers for families">
          <Accordion type="single" collapsible className="max-w-3xl" data-testid="reverse-faqs">
            {p.common_questions.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#1C4358]/12">
                <AccordionTrigger className="text-left text-[18px] font-display font-semibold text-[#1C4358] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[16px] text-[#4B5563] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Block>
      )}

      {/* Voice-search FAQs matched to this topic */}
      <MatchingFaq
        reverseTopic={p.slug}
        serviceLane={p.matched_service_lane}
        franchise={fr?.subdomain || null}
        exclude={(p.common_questions || []).map((f) => f.q)}
        limit={5}
        onItems={setMatchedFaqs}
        title="More questions families ask"
      />

      {/* Matching service lane */}
      {lane && (
        <div className={`${CONTAINER} pb-4`}>
          <div className="rounded-[1rem] bg-[#FBF6EC] border border-[#1C4358]/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <HeartHandshake size={26} className="text-[#9C1E22] shrink-0" />
              {p.matching_lane_copy_html ? (
                <RichTextView html={p.matching_lane_copy_html} className="text-[16px] text-[#252221]" testid="reverse-matching" />
              ) : (
                <p className="text-[16px] text-[#252221]">{p.matching_lane_copy}</p>
              )}
            </div>
            <Link to={`/services/${lane.slug}${locSlug ? `?franchise=${fr?.subdomain || ""}` : ""}`} data-testid="reverse-lane-link"
              className="mmm-tap shrink-0 inline-flex items-center gap-1.5 px-5 h-12 rounded-full text-[15px] font-semibold text-white bg-[#1C4358] hover:bg-[#163645] transition-colors">
              Explore {lane.name} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}

      {/* Lead form */}
      <section id="rs-form" className="py-14 sm:py-20 scroll-mt-20" data-testid="reverse-form-section">
        <div className={CONTAINER}>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Free 1-hour consultation</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#1C4358] leading-tight">You don't have to figure this out alone.</h2>
              <p className="mt-4 text-lg text-[#4B5563] leading-relaxed">Share your situation and a caring guide will reach out to listen and help you find the next right step — no cost, no pressure.</p>
              {phone && (
                <a href={telHref(phone)} className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-14 rounded-full text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors">
                  <Phone size={18} /> Or call {phone}
                </a>
              )}
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-[1.2rem] bg-white border border-[#1C4358]/10 shadow-md shadow-[#1C4358]/10 p-6 sm:p-9">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#9C1E22]/10 text-[#9C1E22]"><Sparkles size={24} /></span>
                  <div>
                    <h3 className="text-xl font-display font-semibold text-[#1C4358]">Tell us what's happening</h3>
                    <p className="text-[14px] text-[#4B5563]">We usually respond the same day.</p>
                  </div>
                </div>
                <IntakeForm
                  cta={ctaLabel}
                  source="reverse-search"
                  campaign={`reverse-search:${p.slug}${data.location ? ` · ${data.location.slug}` : ""}`}
                  franchise={fr?.subdomain || null}
                  extra={{
                    reverse_search_topic: p.slug,
                    matched_service_lane: p.matched_service_lane,
                    location_slug: data.location?.slug || null,
                    ab_variation: ab.variation,
                  }}
                  submitLabel="Request my free consultation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
